import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '@/utils/money';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ItemListOption } from '@/contexts/item-context-types';
import { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import { OrderableItemListItemImage } from './orders/orderable-item-list-item-image';
import { StyleProp, Text, TextStyle, View, ViewStyle } from 'react-native';
import { useItemDisplayOptions } from '@/contexts/new-order-context';

interface Props {
  additionalSubtitle?: string;
  additionalSubtitleStyle?: StyleProp<TextStyle>;
  option: ItemListOption;
  style?: StyleProp<ViewStyle>;
  unitPriceInMicros?: number | null;
}

export const ScannerVariantPackListItem: React.FC<Props> = ({
  additionalSubtitle,
  additionalSubtitleStyle,
  option,
  style,
  unitPriceInMicros,
}) => {
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const [styles] = useStyles();
  const metadata = option.metadata!;
  const packUnit = metadata.packUnit;
  const displayUnit = LOCALIZED_PACK_UNITS[packUnit] || packUnit;

  return (
    <View style={[styles.container, style]}>
      <OrderableItemListItemImage
        alt={[metadata.externalID, displayUnit].filter(Boolean).join(' • ')}
        hidePlaceholderImages={hidePlaceholderImages}
        imageURLs={metadata.photoURLs ?? []}
        style={styles.imagePreview}
      />
      <View style={styles.details}>
        <Text
          numberOfLines={1}
          style={[TYPOGRAPHY.listTitle, FONTS.primarySemiBold]}
        >
          {metadata.displayName}
        </Text>
        <Text
          numberOfLines={1}
          style={[TYPOGRAPHY.small, FONTS.primary, styles.subtitle]}
        >
          {unitPriceInMicros ? (
            <>
              <Text style={styles.price}>
                {formatCurrency(
                  metadata.unitCount * unitPriceInMicros,
                  metadata.currencyCode ?? DEFAULT_CURRENCY_CODE,
                )}
              </Text>
              <Text> • </Text>
            </>
          ) : null}
          {metadata.externalID ? `${metadata.externalID} • ` : ''}
          <Text style={styles.capitalizedText}>{displayUnit}</Text>
          {additionalSubtitle ? (
            <Text
              style={additionalSubtitleStyle}
            >{` • ${additionalSubtitle}`}</Text>
          ) : null}
        </Text>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  capitalizedText: {
    textTransform: 'capitalize',
  },
  container: {
    alignItems: 'flex-start',
    flexDirection: 'row',
  },
  details: {
    alignSelf: 'center',
    flex: 1,
    marginLeft: 16,
  },
  imagePreview: {
    borderColor: theme.sk.color.border.image,
    borderRadius: 8,
    borderWidth: 1,
  },
  price: {
    color: theme.sk.color.text.primary,
  },
  subtitle: {
    color: theme.sk.color.text.tertiary,
  },
}));
